import { ReactNode, useEffect, useState, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useWorkspaceInit } from '@/hooks/useWorkspaceInit';
import { useWorkspaceScope } from '@/contexts/useWorkspaceScope';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useHardMode } from '@/contexts/HardModeContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { NotificationCenter } from '@/components/notifications/NotificationCenter';
import { AIPanel } from '@/components/ai/AIPanel';
import { CommandPalette } from '@/components/CommandPalette';
import { CreateLeadModal } from '@/components/leads/CreateLeadModal';
import { CSVImportModal } from '@/components/leads/CSVImportModal';
import { QuickAddDealModal } from '@/components/deals/QuickAddDealModal';
import { CreateWorkspaceModal } from '@/components/workspace/CreateWorkspaceModal';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Logo } from '@/components/ui/Logo';
import { TrialBanner } from '@/components/trial/TrialBanner';
import { TrialFirstEntryModal } from '@/components/trial/TrialFirstEntryModal';
import { TrialExpiredPage } from '@/components/trial/TrialExpiredPage';
import { WorkspaceScopeSwitcher } from '@/components/workspace/WorkspaceScopeSwitcher';
import { ProUnlockModal } from '@/components/workspace/ProUnlockModal';
import { CompleteProSetupModal } from '@/components/workspace/CompleteProSetupModal';
import { ThemeToggle } from '@/components/theme-toggle';
import { useTrackersBootstrap } from '@/hooks/useTrackersBootstrap';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Calendar,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Loader2,
  ChevronDown,
  Kanban,
  Zap,
  User,
  Building2,
  TrendingUp,
  Command,
  AlertCircle,
  RefreshCw,
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { normalizeError } from '@/lib/errors';

interface AppLayoutProps {
  children: ReactNode;
}

const navItems = [
  { path: '/app', label: 'Today', icon: Zap },
  { path: '/app/leads', label: 'Leads', icon: Users },
  { path: '/app/pipeline', label: 'Revenue Flow', icon: Kanban },
  { path: '/app/calendar', label: 'Calendar', icon: Calendar },
  { path: '/app/keep-warm', label: 'Keep Warm', icon: MessageSquare },
  { path: '/app/dashboard', label: 'Dashboard', icon: BarChart3 },
  { path: '/app/assistant', label: 'Assistant', icon: Sparkles },
];

const hardModeNavItems = [
  { path: '/app', label: 'Today', icon: Zap },
];

export default function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, signOut } = useAuth();
  const { isLoading: workspaceInitLoading, error: workspaceInitError, activeWorkspaceId } = useWorkspaceInit();
  const { workspace, profile, loading: workspaceLoading } = useWorkspace();
  const { workspaces, isLoading: workspacesLoading, refetchWorkspaces } = useWorkspaceScope();
  const { isHardMode } = useHardMode();
  const { 
    accountStatus, 
    trialEndDate, 
    firstAppEntryShown, 
    isExpired, 
    loading: trialLoading,
    markFirstEntryShown 
  } = useTrialStatus();
  const { canBypassPaywall, canBypassTrial, isInternal, isPro, workspaceLimit, plan } = useEntitlements();
  useTrackersBootstrap();

  // CRITICAL: All hooks must be declared BEFORE any early returns to prevent React error #310
  // This ensures the same number of hooks are called on every render
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [importCSVOpen, setImportCSVOpen] = useState(false);
  const [addDealOpen, setAddDealOpen] = useState(false);
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [showEntrance, setShowEntrance] = useState(false);
  const [showTrialWelcome, setShowTrialWelcome] = useState(false);
  const [showProSetup, setShowProSetup] = useState(false);

  // CRITICAL: All useEffect hooks must be declared BEFORE any early returns
  // This ensures the same number of hooks are called on every render
  
  // Check for first entry in session (entrance animation)
  useEffect(() => {
    const hasEnteredThisSession = sessionStorage.getItem('app_entered');
    if (!hasEnteredThisSession && user && !authLoading && !workspaceInitLoading && activeWorkspaceId) {
      setShowEntrance(true);
      sessionStorage.setItem('app_entered', 'true');
      const timer = setTimeout(() => setShowEntrance(false), 500);
      return () => clearTimeout(timer);
    }
  }, [user, authLoading, workspaceInitLoading, activeWorkspaceId]);

  // Auto-provision workspaces for INTERNAL users (automatic, no modal)
  useEffect(() => {
    if (!user?.id || workspacesLoading || workspaceLimit === 0 || !isInternal) return;
    if (!profile?.is_internal) return; // Double-check from profile

    const currentCount = workspaces.length;
    const shouldAutoProvision = workspaceLimit === 3 && currentCount === 1;

    if (shouldAutoProvision) {
      // Auto-provision for internal users
      supabase.rpc('ensure_pro_workspaces')
        .then(({ data, error }) => {
          if (error) {
            console.error('[AppLayout] Auto-provision error:', error);
            return;
          }
          if (data && (data as any).success && (data as any).created > 0) {
            // Refetch workspaces
            refetchWorkspaces();
          }
        });
    }
  }, [user?.id, workspaceLimit, workspaces.length, workspacesLoading, isInternal, profile?.is_internal]);

  // Check for Pro setup modal (NON-INTERNAL Pro users only)
  // Show if: Pro/Trial (workspaceLimit = 3) AND has only 1 workspace AND not seen yet AND NOT internal
  useEffect(() => {
    if (!user?.id || workspacesLoading || workspaceLimit === 0 || isInternal) return;

    const storageKey = `pro_setup_done:${user.id}`;
    const hasSeenModal = localStorage.getItem(storageKey) === 'true';
    const currentCount = workspaces.length;
    const shouldShow = workspaceLimit === 3 && currentCount === 1 && !hasSeenModal;

    if (shouldShow) {
      setShowProSetup(true);
    }
  }, [user?.id, workspaceLimit, workspaces.length, workspacesLoading, isInternal]);

  const handleCloseProSetup = (skip: boolean = false) => {
    if (user?.id && skip) {
      localStorage.setItem(`pro_setup_done:${user.id}`, 'true');
    }
    setShowProSetup(false);
  };

  // Check for first app entry (trial welcome modal)
  // Show ONLY if: trial is active AND welcome not seen yet
  // INTERNAL USER BYPASS: Skip trial welcome modal for internal users
  // INVITE BYPASS: Skip when user just accepted an invite (joined existing workspace)
  useEffect(() => {
    if (canBypassTrial) {
      setShowTrialWelcome(false);
      return;
    }
    const inviteJustAccepted = sessionStorage.getItem('dsos_invite_just_accepted') === 'true';
    if (inviteJustAccepted) {
      sessionStorage.removeItem('dsos_invite_just_accepted');
      setShowTrialWelcome(false);
      toast.success('Invite accepted');
      return;
    }
    if (
      !trialLoading &&
      accountStatus === 'trial' &&
      !firstAppEntryShown &&
      trialEndDate &&
      !isExpired
    ) {
      setShowTrialWelcome(true);
    } else {
      setShowTrialWelcome(false);
    }
  }, [canBypassTrial, trialLoading, accountStatus, firstAppEntryShown, trialEndDate, isExpired]);

  const handleSignOut = async () => {
    sessionStorage.removeItem('app_entered');
    await signOut();
    navigate('/');
  };

  const handleCloseTrialWelcome = async () => {
    await markFirstEntryShown();
    setShowTrialWelcome(false);
  };

  // ProtectedRoute ensures user exists, but keep this as safety check
  if (!user) return null;

  // Show loading screen while workspace is initializing
  if (workspaceInitLoading || authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Setting up your workspace...</p>
        <p className="text-xs text-muted-foreground">This should only take a moment.</p>
      </div>
    );
  }

  // Show error screen if workspace initialization failed
  if (workspaceInitError) {
    const err = normalizeError(workspaceInitError, 'An unexpected error occurred while setting up your workspace.');
    console.error('[AppLayout] workspace setup error:', err.raw);
    
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 px-4">
        <div className="max-w-md w-full text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-destructive" />
            </div>
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold">We couldn't set up your workspace</h1>
            <p className="text-muted-foreground">
              {err.message}
            </p>
            {err.code && (
              <p className="text-xs text-muted-foreground">Error code: {err.code}</p>
            )}
          </div>
          <div className="pt-4">
            <Button 
              onClick={handleSignOut}
              variant="outline"
              className="w-full"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Block app access if no active workspace (should not happen after successful init)
  if (!activeWorkspaceId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Initializing workspace...</p>
      </div>
    );
  }

  // If trial has expired, show the expired page
  // INTERNAL USER BYPASS: Skip trial expired page for internal users
  // PRO USER BYPASS: Skip trial expired page for Pro users (account_status = 'active')
  // Only block if explicitly required - don't block on null/undefined data
  // Note: ProtectedRoute already handles redirect for expired users without Pro/Internal access
  // This is a fallback for edge cases where user might still see the app
  if (!canBypassTrial && !isPro && isExpired && accountStatus === 'trial' && trialEndDate) {
    return <TrialExpiredPage />;
  }

  // Show trial banner only if we have valid trial data AND user is not internal
  const showTrialBanner = !canBypassTrial && accountStatus === 'trial' && trialEndDate && !isExpired;

  return (
    <motion.div 
      className="min-h-screen bg-background flex flex-col"
      initial={showEntrance ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {/* Trial Welcome Modal */}
      {trialEndDate && (
        <>
          <TrialFirstEntryModal
            open={showTrialWelcome}
            onClose={handleCloseTrialWelcome}
            trialEndDate={trialEndDate}
            userName={profile?.name?.split(' ')[0]}
          />

          {/* Pro Unlock Modal - Shows when user becomes Pro/Trial */}
          <ProUnlockModal />

          {/* Complete Pro Setup Modal - Shows for Pro/Trial/Internal users with 1 workspace */}
          <CompleteProSetupModal
            open={showProSetup}
            onOpenChange={(open) => {
              if (!open) {
                handleCloseProSetup(true); // Mark as seen when closed
              } else {
                setShowProSetup(true);
              }
            }}
          />
        </>
      )}

      {/* Command Palette */}
      <CommandPalette 
        onCreateLead={() => setCreateLeadOpen(true)} 
        onAddDeal={() => setAddDealOpen(true)}
        onImportCSV={() => setImportCSVOpen(true)}
        onCreateWorkspace={() => setCreateWorkspaceOpen(true)}
      />
      
      {/* Create Lead Modal */}
      <CreateLeadModal open={createLeadOpen} onOpenChange={setCreateLeadOpen} />
      
      {/* CSV Import Modal */}
      <CSVImportModal open={importCSVOpen} onOpenChange={setImportCSVOpen} />
      
      {/* Add Deal Modal */}
      <QuickAddDealModal open={addDealOpen} onOpenChange={setAddDealOpen} />

      {/* Create Workspace Modal */}
      <CreateWorkspaceModal 
        open={createWorkspaceOpen} 
        onOpenChange={setCreateWorkspaceOpen} 
      />

      {/* Trial Banner */}
      {showTrialBanner && <TrialBanner trialEndDate={trialEndDate} />}

      {/* Top Navigation - Desktop - Tighter header */}
      <header className="hidden md:flex h-12 border-b border-border/40 bg-card/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 flex items-center justify-between gap-4">
          {/* Logo & Workspace - Compact */}
          <div className="flex items-center gap-6">
            <Link to="/app" className="flex items-center gap-2.5">
              <Logo size="sm" showText={true} className="text-foreground" />
            </Link>
            
            <div className="h-4 w-px bg-border hidden md:block" />
            <div className="hidden md:flex items-center gap-2">
              <WorkspaceScopeSwitcher />
            </div>
          </div>

          {/* Navigation Links - Tighter spacing */}
          <nav className="flex items-center gap-0.5">
            {(isHardMode ? hardModeNavItems : navItems).map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path === '/app' && location.pathname === '/app/today');
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'h-8 px-3 gap-1.5 text-xs font-medium transition-all duration-[var(--duration-standard)] ease-[var(--ease-hover)]',
                      isActive 
                        ? 'bg-primary/10 text-primary hover:bg-primary/15' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                  >
                    <item.icon className="w-3.5 h-3.5" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </nav>

          {/* Command + Notification Center + User Menu - Compact */}
          <div className="flex items-center gap-1">
            {/* Command Palette Trigger - Compact ⌘K button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => {
                const event = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
                document.dispatchEvent(event);
              }}
              title="Open command palette (⌘K)"
            >
              <Command className="w-4 h-4" />
            </Button>
            
            {/* Brief button - Only on large screens (>=1200px) */}
            <div className="hidden xl:block">
              <AIPanel />
            </div>

            <ThemeToggle className="h-8 w-8 shrink-0" />

            <NotificationCenter />
            
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 px-2 gap-1.5">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="max-w-24 truncate text-xs hidden sm:inline">{profile?.name || user.email?.split('@')[0]}</span>
                <ChevronDown className="w-3 h-3 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span>{profile?.name}</span>
                  <span className="text-xs font-normal text-muted-foreground">{user.email}</span>
                </div>
              </DropdownMenuLabel>
              {workspace && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-3 h-3" />
                      <span>{workspace.name}</span>
                    </div>
                  </DropdownMenuLabel>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/app/settings" className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content - Wrapped in ErrorBoundary */}
      <main className="flex-1 pb-20 md:pb-0 bg-background min-h-screen">
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </main>

      {/* Bottom Navigation - Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-card border-t flex items-center justify-around px-2 z-50">
        {(isHardMode ? hardModeNavItems : navItems).slice(0, 5).map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors',
                isActive ? 'text-primary' : 'text-muted-foreground'
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs font-medium">{item.label}</span>
            </Link>
          );
        })}
        <Link
          to="/app/settings"
          className={cn(
            'flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors',
            location.pathname === '/app/settings' ? 'text-primary' : 'text-muted-foreground'
          )}
        >
          <Settings className="w-5 h-5" />
          <span className="text-xs font-medium">Settings</span>
        </Link>
      </nav>
    </motion.div>
  );
}
